/** Calendar/display contract shared by API, web and native cache fallback. */
export interface PracticeStreak {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  timezone: string | null;
  asOfDate: string | null;
}

export const PRACTICE_RETRY_MS = 7 * 24 * 60 * 60 * 1000;

export function validTimezone(value: unknown): value is string {
  if (typeof value !== "string" || !value || value.length > 100) return false;
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export function deviceTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function calendarDay(time: Date | number, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(time);
  const part = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function validDay(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}

export function effectiveStreak(streak: PracticeStreak, now: number = Date.now()): PracticeStreak {
  if (!streak.timezone) return { ...streak, asOfDate: null };
  const today = calendarDay(now, streak.timezone);
  const yesterday = new Date(Date.parse(`${today}T00:00:00Z`) - 86400000)
    .toISOString()
    .slice(0, 10);
  return {
    ...streak,
    asOfDate: today,
    currentStreak:
      streak.lastActivityDate && streak.lastActivityDate >= yesterday ? streak.currentStreak : 0,
  };
}
